/**
 * Card "Informações" do perfil: email, telefone, idade, altura, peso,
 * IMC (calculado), mensalidade, dia de vencimento, sexo.
 *
 * Em modo edição renderiza TextInputs editáveis (controlados via form).
 * Em modo view renderiza linhas label/valor + IMC calculado on the fly.
 *
 * Props:
 *   - student: aluno (modo view lê todos os campos daqui)
 *   - form: estado do formulário (modo edição lê daqui)
 *   - isEditing: alterna entre modos
 *   - onFormChange: updater do form. Recebe um delta parcial
 *     (ex: `{ phone: '999' }`) — o orquestrador faz o spread.
 *
 * Decisões:
 *   - IMC só renderiza se houver altura E peso (sem placeholder).
 *   - Componente `Field` está inline aqui (não exportado) porque é
 *     só uma linha label/valor — não vale criar arquivo separado.
 */

import { memo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Student } from '@/lib/types';
import { EditForm, SEX_LABEL, SEX_OPTIONS, calcAge } from './helpers';
import { sharedStyles } from './styles';

interface Props {
  student: Student;
  form: EditForm;
  isEditing: boolean;
  onFormChange: (patch: Partial<EditForm>) => void;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <View style={sharedStyles.fieldRow}>
      <Text style={sharedStyles.fieldLabel}>{label}</Text>
      <Text style={sharedStyles.fieldValue}>{value || '—'}</Text>
    </View>
  );
}

function InfoSectionImpl({ student, form, isEditing, onFormChange }: Props) {
  return (
    <>
      <Text style={sharedStyles.sectionTitle}>Informações</Text>
      <View style={sharedStyles.card}>
        {isEditing ? (
          <>
            <Text style={sharedStyles.fieldLabel}>Email</Text>
            <TextInput
              style={sharedStyles.input}
              value={form.email}
              onChangeText={v => onFormChange({ email: v })}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <Text style={sharedStyles.fieldLabel}>Telefone</Text>
            <TextInput
              style={sharedStyles.input}
              value={form.phone}
              onChangeText={v => onFormChange({ phone: v })}
              keyboardType="phone-pad"
              placeholder="(11) 99999-9999"
            />
            <Text style={sharedStyles.fieldLabel}>Data de nascimento</Text>
            <TextInput
              style={sharedStyles.input}
              value={form.birth_date}
              onChangeText={v => onFormChange({ birth_date: v })}
              placeholder="AAAA-MM-DD"
            />
            <Text style={sharedStyles.fieldLabel}>Altura (cm)</Text>
            <TextInput
              style={sharedStyles.input}
              value={form.height_cm}
              onChangeText={v => onFormChange({ height_cm: v })}
              keyboardType="numeric"
              placeholder="175"
            />
            <Text style={sharedStyles.fieldLabel}>Peso (kg)</Text>
            <TextInput
              style={sharedStyles.input}
              value={form.weight_kg}
              onChangeText={v => onFormChange({ weight_kg: v })}
              keyboardType="numeric"
              placeholder="70"
            />
            <Text style={sharedStyles.fieldLabel}>Mensalidade (R$)</Text>
            <TextInput
              style={sharedStyles.input}
              value={form.monthly_fee}
              onChangeText={v => onFormChange({ monthly_fee: v })}
              keyboardType="decimal-pad"
              placeholder="Deixe vazio se não houver mensalidade fixa"
            />
            <Text style={sharedStyles.fieldLabel}>Vencimento (dia do mês)</Text>
            <TextInput
              style={sharedStyles.input}
              value={form.due_day}
              onChangeText={v => onFormChange({ due_day: v })}
              keyboardType="numeric"
              placeholder="1–28"
            />
            <Text style={sharedStyles.fieldLabel}>Sexo biológico</Text>
            <View style={styles.sexRow}>
              {SEX_OPTIONS.map(opt => (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.sexBtn, form.sex === opt.value && styles.sexBtnActive]}
                  onPress={() => onFormChange({ sex: form.sex === opt.value ? null : opt.value })}
                >
                  <Text style={[styles.sexBtnText, form.sex === opt.value && styles.sexBtnTextActive]}>
                    {opt.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </>
        ) : (
          <>
            <Field label="Email" value={student.email} />
            <Field label="Telefone" value={student.phone ?? ''} />
            <Field label="Idade" value={calcAge(student.birth_date)} />
            <Field label="Altura" value={student.height_cm != null ? `${student.height_cm} cm` : ''} />
            <Field label="Peso" value={student.weight_kg != null ? `${student.weight_kg} kg` : ''} />
            <Field
              label="IMC"
              value={
                student.height_cm && student.weight_kg
                  ? (() => {
                      const imc = student.weight_kg / Math.pow(student.height_cm / 100, 2);
                      const label =
                        imc < 18.5 ? 'Abaixo do peso'
                        : imc < 25 ? 'Normal'
                        : imc < 30 ? 'Sobrepeso'
                        : 'Obesidade';
                      return `${imc.toFixed(1)} — ${label}`;
                    })()
                  : ''
              }
            />
            <Field label="Sexo" value={student.sex ? SEX_LABEL[student.sex] : ''} />
          </>
        )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  sexRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },
  sexBtn: {
    flex: 1,
    paddingVertical: 9,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#ddd',
    alignItems: 'center',
  },
  sexBtnActive: { borderColor: '#C8102E', backgroundColor: '#FFF0F2' },
  sexBtnText: { fontSize: 12, fontWeight: '600', color: '#888' },
  sexBtnTextActive: { color: '#C8102E' },
});

export const InfoSection = memo(InfoSectionImpl);
